<template>
  <thead class="sp-table-head" :class="classModifiers" part="head">
    <tr>
      <th
        v-for="column in columns"
        :key="column.field"
        scope="col"
        part="header-cell"
        class="sp-table-head__cell"
        :class="cellClass(column)"
        :style="cellStyle(column)"
        :aria-sort="ariaSortFor(column)"
      >
        <slot :name="`header-${column.field}`" :column="column"></slot>
      </th>
    </tr>
  </thead>
</template>

<script setup>
import { computed } from "vue";

import { cellClass, cellStyle } from "#ce/components/SpTable/columns.js";

const props = defineProps({
  columns: {
    type: Array,
    default: () => [],
  },
  /**
   * Field the table is sorted by, which heads the column carrying `aria-sort`.
   */
  sortField: {
    type: String,
    default: undefined,
  },
  /**
   * Direction of the current sort, "asc" or "desc".
   */
  direction: {
    type: String,
    default: "desc",
  },
  stickyHeader: {
    type: Boolean,
    default: false,
  },
});

const classModifiers = computed(() => ({ "--sticky": props.stickyHeader }));

function ariaSortFor(column) {
  if (!column.sortable) {
    return undefined;
  }

  return props.sortField === column.field ? `${props.direction}ending` : "none";
}
</script>

<style lang="scss" scoped>
@use "../shared/cells";

.sp-table-head {
  &__cell {
    @include cells.cell-geometry;
    @include cells.cell-alignment;

    // A button in a header brings its own type scale and role colour. These inherit into the
    // built-in sort control and into slotted content alike, so a header reads the same however it
    // is filled.
    --sp-comp-button-xx-small-font-size: 1em;
    --sp-comp-button-x-small-font-size: 1em;
    --sp-comp-button-font-weight: var(--sp-comp-table-header-font-weight, var(--sp-ref-font-weight-semi-bold));
    --sp-comp-button-primary-variant-text-text-color: var(--table-header-color);
    --sp-comp-button-primary-hover-variant-text-text-color: var(--table-header-color);
    --sp-comp-icon-fill: var(--table-header-color);
    --fill: var(--table-header-color);

    color: var(--table-header-color);
    font-size: var(--sp-comp-table-header-font-size, inherit);
    font-weight: var(--sp-comp-table-header-font-weight, var(--sp-ref-font-weight-semi-bold));
    background-color: var(--sp-comp-table-header-background-color, var(--sp-sys-color-surface));
  }

  &.--sticky &__cell {
    position: sticky;
    top: 0;
    z-index: 1;
  }
}
</style>
